import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Column,
  Columns,
  Hidden,
  Stack,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import RequestingCompany from "./FormComponents/RequestingCompany";
import ProjectName from "./FormComponents/ProjectName";
import PropertyType from "./FormComponents/PropertyType";
import AddressField from "./FormComponents/AddressField";
import PropertyAccess from "./FormComponents/PropertyAccess";
import ServiceTypes from "./FormComponents/ServiceTypes";
import ElecSectionInPremise from "./FormComponents/ElecSectionInPremise";
import { IFormFunctionProps, IFormProps } from "./FormAccordionContainer";
import { Field, FormikErrors, useFormikContext, yupToFormErrors } from "formik";
import {
  getFormattedAddress,
  isAllowedForQldElec,
  isAllowedStateForElec,
} from "../Premises/helpers";
import FormContext, {
  ElectricityService,
  GasServices,
} from "src/context/FormContext";
import { premiseDraftValidationSchema } from "./FormComponents/Validations";
import {
  formDataToFormikMapper,
  getSolarInitialValues,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";
import {
  EnergyType,
  ErrorTypeEnum,
  FormValues,
  GasOptionEnum,
  PremiseAddressValues,
  Service,
} from "./types";
import { State } from "../Premises/types";
import {
  actAddressEmptyFields,
  nswAddressEmptyFields,
  showUpdatePremiseAlert,
} from "./helper";
import GasSectionInPremise from "./FormComponents/GasSectionInPremise";
import {
  getElectricityServiceContext,
  getGasServicesContext,
  getIsAccountReadOnly,
} from "./helpers";
import ContactField from "./FormComponents/ContactField";
import ReadOnlyElecSectionInPremise from "./ReadOnlyElecSectionInPremise";
import ReadOnlyPropertyAccessInPremise from "./ReadOnlyPropertyAccessInPremise";
import { useAppContext } from "../../context/AppContext";
import ClaimDialog from "./Claim/ClaimDialog";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import HomeUserClaimDialog from "./Claim/HomeUserClaimDialog";
import ConfirmCreateDuplicatePremiseModal from "../Premises/ConfirmCreateDuplicatePremiseModal";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import { PermissionEnum } from "../Permissions/types";
import PermissionsControlledWrapper from "../Permissions/PermissionsControlledWrapper";

interface IPremiseBlockProps extends IFormProps, IFormFunctionProps {
  savingBlock: boolean;
  servicesSelected: EnergyType[];
  state: string;
  postcode: string;
  errors: FormikErrors<any>;
  validateForm: () => Promise<FormikErrors<any>>;
  setPremiseId: React.Dispatch<React.SetStateAction<string | null>>;
  premiseId: string | null;
  setFormLoadValues: React.Dispatch<React.SetStateAction<FormValues>>;
  gasOptions: GasOptionEnum[];
  setIsDuplicate: React.Dispatch<React.SetStateAction<boolean>>;
  isDuplicate?: boolean | false;
  duplicateSourceRequestId?: string;
  homeUser: boolean;
}

const PremiseBlock = (props: IPremiseBlockProps) => {
  const {
    setFieldValue,
    submitForm,
    validateForm,
    submitClicked,
    setSubmitClicked,
    savingBlock,
    servicesSelected,
    state,
    postcode,
    errors,
    premiseId,
    setPremiseId,
    setFormLoadValues,
    gasOptions,
    setIsDuplicate,
    isDuplicate,
    duplicateSourceRequestId,
    homeUser,
  } = props;
  const { values, setErrors } = useFormikContext<FormValues>();
  const { setDisplayingSnackbar, setDisplayingAlertBanner, formState } =
    React.useContext(FormContext);
  const { context } = useAppContext();
  const [claimDialogOpen, setClaimDialogOpen] = useState<boolean>(false);
  const [
    confirmCreateDuplicatePremiseOpen,
    setConfirmCreateDuplicatePremiseOpen,
  ] = useState<boolean>(false);
  const [addressFromDuplicate, setAddressFromDuplicate] = useState<
    PremiseAddressValues | undefined
  >(undefined);

  const accountPayers = React.useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "premiseBlock",
    !!premiseId
  );

  useEffect(() => {
    if (context.project) {
      setFieldValue("projectId", context.project.id);
      setFieldValue("projectName", context.project.name);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  const savePremiseBlock = async (
    formValues: FormValues,
    services?: {
      electricityService: ElectricityService;
      gasServices: GasServices;
    }
  ) => {
    try {
      const { data } = await apiClient.post(
        "/draft/premise",
        premiseFormFieldMapper(
          formValues,
          services,
          undefined,
          true,
          undefined,
          undefined,
          false
        ),
        {
          params: {
            sourceRequestId: isDuplicate ? duplicateSourceRequestId : null,
          },
        }
      );
      setPremiseId(data.premise.id);

      let mappedFormikValues;

      if (isDuplicate) {
        setIsDuplicate(false);

        mappedFormikValues = formDataToFormikMapper({
          ...data,
          // init solar values for NSW and SA Electricity service
          services: data.services.map((s: Service) => ({
            ...s,
            // Payload is a string
            payload: JSON.stringify({
              ...JSON.parse(s.payload),
              ...getSolarInitialValues(
                s.service_type,
                data.premise.site_details.state as State
              ),
            }),
          })),
        });
      } else {
        mappedFormikValues = formDataToFormikMapper(data);
      }

      setFormLoadValues({
        ...mappedFormikValues,
        siteSupervisors: formValues.siteSupervisors,
        licensedPlumbers: formValues.licensedPlumbers,
      });

      const electricityService = data.services.find(
        (s: Service) => s.service_type === "ELECTRICITY"
      );

      const isCreatedByHomeUser =
        data.premise.id && !data.premise.site_details.requesting_company;
      setFormState((prevState) => ({
        ...prevState,
        state: formValues.premiseAddress.state,
        siteDetails: data.premise.site_details,
        address: getFormattedAddress(data.premise),
        msaNumber: formValues.requestingCompany?.msaNumber ?? "",
        postcode: formValues.premiseAddress.postcode,
        electricityService: getElectricityServiceContext(electricityService),
        gasServices: getGasServicesContext(data.services),
        isAccountReadOnly: getIsAccountReadOnly(
          data.services,
          isCreatedByHomeUser
        ),
      }));
      setOnlineAccountHolderDetails({
        accountPrimaryFirstName: values.accountPrimaryFirstName,
        accountPrimaryLastName: values.accountPrimaryLastName,
        accountPrimaryEmail: values.accountPrimaryEmail,
      });

      setDisplayingSnackbar({
        message: `Premises ${premiseId ? "updated" : "created"}`,
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (e) {
      setDisplayingSnackbar({
        message: `Couldn't ${premiseId ? "update" : "create"} premises`,
        tone: "caution",
      });
      console.error("Failed to save premises:", e);
    }
  };

  useEffect(() => {
    if (submitClicked) {
      submitForm();
    }
    setSubmitClicked(false);
  }, [submitClicked, submitForm, setSubmitClicked]);

  //cleanup for property type pathway ui selections
  useEffect(() => {
    if (!premiseId && !isDuplicate) {
      if (values.premiseAddress.state === State.ACT) {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
          ...actAddressEmptyFields,
        });
      } else if (values.premiseAddress.state === State.NSW) {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
          ...nswAddressEmptyFields,
        });
      } else {
        setFieldValue("premiseAddress", {
          ...values.premiseAddress,
          premiseType: "",
        });
      }
    }
  }, [values.propertyType]);

  const {
    setOnlineAccountHolderDetails,
    setFormState,
    formState: { electricityService, gasServices },
  } = React.useContext(FormContext);

  const isPremiseAddressUnset = (premiseAddress: PremiseAddressValues) => {
    return Object.values(premiseAddress).every(
      (v) => (typeof v === "string" && v.trim() === "") || v === false
    );
  };

  useEffect(() => {
    if (
      isDuplicate &&
      !addressFromDuplicate &&
      !isPremiseAddressUnset(values.premiseAddress)
    ) {
      setAddressFromDuplicate(values.premiseAddress);
    }
  }, [values.premiseAddress]);

  const onConfirmCreateDuplicatePremise = () => {
    savePremiseBlock(values, undefined);
    setConfirmCreateDuplicatePremiseOpen(false);
  };

  const isAddressUpdated = (address: PremiseAddressValues): boolean => {
    return Object.keys(address).some(
      (key) =>
        address[key as keyof PremiseAddressValues] !==
        addressFromDuplicate?.[key as keyof PremiseAddressValues]
    );
  };

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* Premise details */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <Columns collapseBelow="md">
                <RequestingCompany
                  error={errors["requestingCompany"]}
                  setFieldValue={setFieldValue}
                  submitForm={submitForm}
                  validateForm={validateForm}
                  isEditing={!!premiseId}
                  requestingCompany={
                    context.requestingCompany
                      ? context.requestingCompany
                      : values.requestingCompany
                  }
                />
                <ProjectName
                  setFieldValue={setFieldValue}
                  submitForm={submitForm}
                  validateForm={validateForm}
                  requestingCompany={
                    context.requestingCompany
                      ? context.requestingCompany
                      : values.requestingCompany
                  }
                  projectName={values.projectName}
                  projectId={values.projectId}
                />
              </Columns>

              <Field
                id="property-type"
                name="propertyType"
                component={PropertyType}
                isReadOnly={!!premiseId}
              />
              {/* Address */}
              <AddressField
                setFieldValue={setFieldValue}
                submitForm={submitForm}
                validateForm={validateForm}
                isReadOnly={!!premiseId}
              />
              {/* End Address */}
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>
        {/* Safe access */}
        <FormFieldLayout text="Clear access to the property and meter site is essential; otherwise, rescheduling and fees may occur, impacting installation timelines.">
          {electricityService?.isReadOnly ? (
            <ReadOnlyPropertyAccessInPremise />
          ) : (
            <PropertyAccess />
          )}
        </FormFieldLayout>

        {/* End Safe access */}

        {/* Site Supervisor */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ContactField
                contactType="SUPERVISOR"
                name="siteSupervisor"
                label="Site supervisor"
              />
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>
        {/* End Site Supervisor */}

        {/* Services sections */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ServiceTypes
                error={errors["selectedConnections"]}
                isEditing={!!premiseId}
              />
            </Stack>
          </Column>
          <Column width="1/3">
            <Box display="flex" alignItems="stretch" height="full">
              <>
                <Hidden below="lg">
                  {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="medium"
                    height="full"
                  />
                  {/* End Faux vertical <Divider> */}
                </Hidden>
                {!electricityService?.isReadOnly &&
                state === "VIC" &&
                values.selectedConnections.includes("Electricity") ? (
                  <Box
                    alignSelf="center"
                    paddingBottom={{
                      xs: "large",
                      lg: "none",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      For Powercor, Citipower, United Energy and Ausnet, you
                      will need an DB application ID and then{" "}
                      <TextLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setClaimDialogOpen(!claimDialogOpen);
                        }}
                      >
                        <Strong>claim it here</Strong>
                      </TextLink>
                    </Text>
                  </Box>
                ) : null}
                {!electricityService?.isReadOnly &&
                state === "QLD" &&
                values.selectedConnections.includes("Electricity") &&
                !isAllowedForQldElec(postcode) ? (
                  <Box
                    alignSelf="center"
                    paddingBottom={{
                      xs: "large",
                      lg: "none",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      For an electricity new connection in QLD, you will need to
                      have the EWR number from Energex and then{" "}
                      <TextLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setClaimDialogOpen(!claimDialogOpen);
                        }}
                      >
                        <Strong>claim it here</Strong>
                      </TextLink>
                    </Text>
                  </Box>
                ) : null}
              </>
            </Box>
          </Column>
        </Columns>
        {/* End Services sections */}

        {/* End Premise details */}
        {/* Proof of address section appears here */}

        {servicesSelected.map((service, i) => {
          if (service === "Electricity") {
            if (
              isAllowedStateForElec(state) ||
              isAllowedForQldElec(postcode) ||
              formState?.electricityService?.channel === "CLAIM"
            ) {
              return electricityService?.isReadOnly ? (
                <ReadOnlyElecSectionInPremise homeUser={false} />
              ) : (
                <ElecSectionInPremise
                  key={`electricity-${i}`}
                  service={service}
                  setFieldValue={setFieldValue}
                  submitForm={submitForm}
                  validateForm={validateForm}
                  isEditing={!!premiseId}
                />
              );
            }
            return null;
          }
          if (service === "Gas") {
            if (state) {
              return <GasSectionInPremise key={`gas-${i}`} />;
            }
          }
          return null;
        })}
      </Stack>
      {!(
        !electricityService?.isReadOnly &&
        state === "QLD" &&
        values.selectedConnections.includes("Electricity") &&
        !isAllowedForQldElec(postcode)
      ) && (
        <Box paddingY={["small", "medium"]}>
          <Stack space="xlarge">
            {showUpdatePremiseAlert(
              accountPayers,
              Boolean(values.accountId),
              !!premiseId
            ) && <UpdatePremiseAlert isPremise />}
            <PermissionsControlledWrapper
              requiredPermission={PermissionEnum.CREATE_REQUEST}
            >
              {savingBlock ? (
                <Button spinner>Action</Button>
              ) : (
                <Button
                  onClick={() => {
                    setFormState((prevState) => ({
                      ...prevState,
                      validationSchema: premiseDraftValidationSchema,
                    }));
                    //Update premise
                    const services = {
                      electricityService: electricityService ?? {},
                      gasServices: gasServices!,
                    };
                    premiseDraftValidationSchema
                      .validate(values, {
                        abortEarly: false,
                        context: {
                          gasOptions,
                          servicesSelected,
                        },
                      })
                      .then(() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions,babel/no-unused-expressions
                        isDuplicate && !isAddressUpdated(values.premiseAddress)
                          ? setConfirmCreateDuplicatePremiseOpen(true)
                          : savePremiseBlock(
                              values,
                              premiseId ? services : undefined
                            );
                      })
                      .catch((e) => {
                        setErrors(yupToFormErrors(e));
                      });
                  }}
                >
                  {premiseId ? "Update Premises" : "Create Premises"}
                </Button>
              )}
            </PermissionsControlledWrapper>
          </Stack>
        </Box>
      )}
      {/* </Accordion> */}
      {/* End Premise section */}
      {homeUser ? (
        <HomeUserClaimDialog
          open={claimDialogOpen}
          setOpen={setClaimDialogOpen}
        />
      ) : (
        <ClaimDialog open={claimDialogOpen} setOpen={setClaimDialogOpen} />
      )}
      <ClaimDialog open={claimDialogOpen} setOpen={setClaimDialogOpen} />
      <ConfirmCreateDuplicatePremiseModal
        open={confirmCreateDuplicatePremiseOpen}
        setOpen={setConfirmCreateDuplicatePremiseOpen}
        onConfirmCreatePremise={onConfirmCreateDuplicatePremise}
      />
    </Stack>
  );
};

export default PremiseBlock;
